import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, MenuItem, TextField, Button, CircularProgress, Alert,
  FormGroup, FormControlLabel, Checkbox, Collapse, IconButton, Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth } from '../../contexts/AuthContext';
import { Device, InferenceModel, InferenceModelClass } from '../../components/devices/types';

interface InferenceSettingsPanelProps {
  device: Device;
  inferenceModels: InferenceModel[];
  onSaved: () => void;
}

const InferenceSettingsPanel: React.FC<InferenceSettingsPanelProps> = ({ device, inferenceModels, onSaved }) => {
  const auth = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [modelId, setModelId] = useState<string>(device.inferenceModelId ?? '');
  const [confidenceText, setConfidenceText] = useState(device.inferenceConfidence != null ? String(device.inferenceConfidence) : '');
  // null = all classes
  const [selectedClasses, setSelectedClasses] = useState<Set<number> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setModelId(device.inferenceModelId ?? '');
    setConfidenceText(device.inferenceConfidence != null ? String(device.inferenceConfidence) : '');
    if (device.inferenceClasses) {
      setSelectedClasses(new Set(device.inferenceClasses.split(',').map(Number).filter((n) => !isNaN(n))));
    } else {
      setSelectedClasses(null);
    }
  }, [device]);

  const modelClasses: InferenceModelClass[] = inferenceModels.find((m) => m.id === modelId)?.classes ?? [];

  const toggleClass = (classIndex: number) => {
    setSelectedClasses((prev) => {
      const current = prev ?? new Set(modelClasses.map((c) => c.classIndex));
      const next = new Set(current);
      if (next.has(classIndex)) next.delete(classIndex);
      else next.add(classIndex);
      return next.size === modelClasses.length ? null : next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const confidence = confidenceText.trim() === '' ? null : Number(confidenceText);
      if (confidence != null && (isNaN(confidence) || confidence < 0 || confidence > 1)) {
        setError('Confidence must be a number between 0 and 1');
        setSaving(false);
        return;
      }
      const body: Device = {
        id: device.id,
        name: device.name,
        deviceTypeId: device.deviceTypeId,
        parentId: device.parentId ?? null,
        inferenceModelId: modelId || null,
        inferenceNodeId: device.inferenceNodeId ?? null,
        inferenceEnabled: device.inferenceEnabled ?? true,
        inferenceConfidence: confidence,
        inferenceClasses: modelId && selectedClasses
          ? Array.from(selectedClasses).sort((a, b) => a - b).join(',')
          : null,
      };
      const res = await fetch(`/api/devices/${device.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.tokens?.accessToken}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Failed to save inference settings');
      }
      onSaved();
      setExpanded(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to save inference settings');
    } finally {
      setSaving(false);
    }
  };

  const currentModelName = inferenceModels.find((m) => m.id === device.inferenceModelId)?.displayName ?? 'None assigned';

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setExpanded((e) => !e)}>
        <Typography variant="subtitle2">
          Inference model: <strong>{currentModelName}</strong>
          {device.inferenceConfidence != null && ` · confidence ${device.inferenceConfidence}`}
        </Typography>
        <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>
      <Collapse in={expanded}>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            select
            label="Inference model"
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value="">None</MenuItem>
            {inferenceModels.map((m) => (
              <MenuItem key={m.id} value={m.id}>{m.displayName}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Confidence threshold (0–1)"
            value={confidenceText}
            onChange={(e) => setConfidenceText(e.target.value)}
            size="small"
            placeholder="0.5"
            helperText="Lower catches more, but with more false positives"
          />

          {modelId && modelClasses.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Classes to detect (unchecked = ignored)</Typography>
              <FormGroup>
                {modelClasses.map((cls) => (
                  <FormControlLabel
                    key={cls.classIndex}
                    control={
                      <Checkbox
                        size="small"
                        checked={!selectedClasses || selectedClasses.has(cls.classIndex)}
                        onChange={() => toggleClass(cls.classIndex)}
                      />
                    }
                    label={cls.className}
                  />
                ))}
              </FormGroup>
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Save inference settings'}
            </Button>
          </Box>
        </Stack>
      </Collapse>
    </Paper>
  );
};

export default InferenceSettingsPanel;
